/**
 * The single text-rendering path shared by the live 3D preview and the
 * print file. Both call `buildTextOptions` — the print version is just the
 * same options uniformly scaled to the print width — so what the customer
 * sees is exactly what the merchant receives.
 */

import { renderTextToCanvas, type TextOptions } from '@printapp/previews-3d';
import type { MokkoningConfig } from './config';

/** Fraction the font shrinks per character beyond the threshold. */
const SHRINK_PER_CHAR = 0.04;
/** Lower bound on the shrink factor — text never drops below this share of the configured size. */
const MIN_SHRINK_FACTOR = 0.35;

/**
 * Font size (in the configured/base canvas space, before print scaling) after
 * applying the length-based auto-shrink. Text at or under the threshold — or a
 * threshold of 0 — renders at the full configured size; each character beyond
 * the threshold reduces the size by {@link SHRINK_PER_CHAR}, floored at
 * {@link MIN_SHRINK_FACTOR}.
 */
export function shrinkFontSize(baseFontSize: number, text: string, threshold: number): number {
    const overflow = text.length - threshold;
    if (threshold <= 0 || overflow <= 0) {
        return baseFontSize;
    }
    const factor = Math.max(MIN_SHRINK_FACTOR, 1 - SHRINK_PER_CHAR * overflow);
    return baseFontSize * factor;
}

export function buildTextOptions(
    cfg: MokkoningConfig,
    text: string,
    canvasWidth: number,
    aspectRatio: number,
): TextOptions {
    const scale = canvasWidth / cfg.text.canvasWidth;
    const fontSize = shrinkFontSize(cfg.text.fontSize, text, cfg.text.shrinkThreshold);
    return {
        text,
        font: cfg.text.font,
        fontWeight: 'normal',
        color: cfg.text.color,
        fontSize: Math.round(fontSize * scale),
        x: Math.round(cfg.text.x * scale),
        y: Math.round(cfg.text.y * scale),
        canvasWidth,
        canvasHeight: Math.round(canvasWidth / aspectRatio),
        backgroundImage: cfg.template.url || undefined,
        backgroundFit: 'fill',
    };
}

export async function generatePrintBlob(
    cfg: MokkoningConfig,
    text: string,
    aspectRatio: number,
): Promise<Blob> {
    const canvas = await renderTextToCanvas(buildTextOptions(cfg, text, cfg.print.width, aspectRatio));
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
            'image/png',
        );
    });
}
